import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  PhoneAndroid as PhoneIcon,
  Notifications as NotificationIcon,
  Send as SendIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { ActivityTypes, logActivity } from '../utils/activityLogger';

const PushNotifications = () => {
  const theme = useTheme();
  const [notification, setNotification] = useState({
    title: '',
    message: '',
    type: 'info',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [sending, setSending] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNotification(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!notification.title.trim() || !notification.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSending(true);
      const notificationData = {
        title: notification.title,
        message: notification.message,
        type: notification.type,
        timestamp: new Date().toISOString(),
      };

      await api.notifications.sendNotification(notificationData);

      // Log activity
      await logActivity({
        action: ActivityTypes.NOTIFICATION_SEND,
        details: `Sent notification: ${notification.title}`,
        metadata: {
          notificationTitle: notification.title,
          notificationType: notification.type,
          notificationBody: notification.message
        }
      });

      toast.success('Notification sent successfully!');
      
      // Reset form
      setNotification({
        title: '',
        message: '',
        type: 'info',
      });
      
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error.response?.data?.message || 'Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <SuccessIcon sx={{ fontSize: 20 }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 20 }} />;
      default:
        return <InfoIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.info.main;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'success':
        return 'Success';
      case 'warning':
        return 'Warning';
      case 'error':
        return 'Error';
      default:
        return 'Information';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
        Push Notifications
      </Typography>
        <Typography variant="body2" color="text.secondary">
          Create and send push notifications to all users. Preview how it will appear on mobile devices.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} md={6}>
      <Paper
            elevation={0}
        sx={{
          p: 4,
              borderRadius: 4,
          bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              height: '100%',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.light})`,
              },
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.light})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  <NotificationIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700} color={theme.palette.text.primary}>
                    Notification Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Create a push notification for all users
                  </Typography>
                </Box>
              </Box>
            </Box>
            
        <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, color: theme.palette.text.primary }}
                >
                  Title <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
                </Typography>
          <TextField
            fullWidth
            name="title"
            value={notification.title}
            onChange={handleChange}
            required
            variant="outlined"
                  placeholder="Enter notification title..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.default, 0.5)
                        : alpha(theme.palette.background.default, 0.8),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.background.default, 0.7)
                          : alpha(theme.palette.background.default, 1),
                      },
                      '&.Mui-focused': {
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 1.5,
                    },
                  }}
                />
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, color: theme.palette.text.primary }}
                >
                  Message <Typography component="span" sx={{ color: 'error.main' }}>*</Typography>
                </Typography>
          <TextField
            fullWidth
            name="message"
            value={notification.message}
            onChange={handleChange}
            required
            multiline
                  rows={6}
            variant="outlined"
                  placeholder="Enter notification message..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.default, 0.5)
                        : alpha(theme.palette.background.default, 0.8),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.background.default, 0.7)
                          : alpha(theme.palette.background.default, 1),
                      },
                      '&.Mui-focused': {
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 1.5,
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {notification.message.length} characters
                </Typography>
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, color: theme.palette.text.primary }}
                >
                  Notification Type
                </Typography>
                <FormControl fullWidth>
            <Select
              name="type"
              value={notification.type}
              onChange={handleChange}
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.default, 0.5)
                        : alpha(theme.palette.background.default, 0.8),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.background.default, 0.7)
                          : alpha(theme.palette.background.default, 1),
                      },
                      '&.Mui-focused': {
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.divider, 0.2),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    }}
                  >
                    <MenuItem value="info">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <InfoIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Information
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            General notification
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="success">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <SuccessIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Success
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Positive updates
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="warning">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Warning
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Important alerts
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="error">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Error
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Critical issues
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
            </Select>
          </FormControl>
              </Box>

              <Box sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end' 
              }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setNotification({
                      title: '',
                      message: '',
                      type: 'info',
                    });
                    setLogoError(false);
                  }}
                  disabled={sending}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderColor: alpha(theme.palette.divider, 0.3),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  Clear
                </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
                  disabled={sending || !notification.title.trim() || !notification.message.trim()}
                  startIcon={!sending && <SendIcon />}
                  sx={{
                    minWidth: 200,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.dark})`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary?.dark || theme.palette.primary.main})`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      background: theme.palette.action.disabledBackground,
                      boxShadow: 'none',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {sending ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                      Sending...
                    </Box>
                  ) : (
                    'Send Notification'
                  )}
            </Button>
          </Box>
        </form>
      </Paper>
        </Grid>

        {/* Phone Mockup Section */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: 'sticky',
              top: 20,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          >
            {/* iPhone 16 Mockup */}
            <Box
              sx={{
                width: { xs: '100%', sm: 375 },
                maxWidth: 375,
                height: 812,
                bgcolor: '#000',
                borderRadius: '47px',
                p: '8px',
                boxShadow: `0 30px 80px ${alpha('#000', 0.5)}, 0 0 0 1px ${alpha('#fff', 0.1)}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* iPhone 16 Screen */}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '39px',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  // iPhone 16 Wallpaper - iOS style gradient
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 20%, #16213e 40%, #0f3460 60%, #16213e 80%, #1a1a2e 100%)'
                    : 'linear-gradient(180deg, #f5f7fa 0%, #c3cfe2 20%, #e0c3fc 40%, #8ec5fc 60%, #e0c3fc 80%, #c3cfe2 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: theme.palette.mode === 'dark'
                      ? 'radial-gradient(ellipse at 50% 0%, rgba(100, 150, 255, 0.2) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(255, 100, 150, 0.15) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(150, 100, 255, 0.1) 0%, transparent 50%)'
                      : 'radial-gradient(ellipse at 50% 0%, rgba(255, 255, 255, 0.4) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(255, 200, 255, 0.3) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(200, 220, 255, 0.3) 0%, transparent 50%)',
                    pointerEvents: 'none',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: theme.palette.mode === 'dark'
                      ? 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.01) 2px, rgba(255, 255, 255, 0.01) 4px)'
                      : 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 0, 0, 0.01) 2px, rgba(0, 0, 0, 0.01) 4px)',
                    pointerEvents: 'none',
                    opacity: 0.3,
                  },
                }}
              >
                {/* Dynamic Island */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 126,
                    height: 37,
                    bgcolor: '#000',
                    borderRadius: '19px',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {/* Dynamic Island Camera/Speaker */}
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#1a1a1a',
                      position: 'absolute',
                      left: 20,
                    }}
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 4,
                      borderRadius: '2px',
                      bgcolor: '#1a1a1a',
                      position: 'absolute',
                      right: 20,
                    }}
                  />
                </Box>

                {/* iOS Lock Screen Date & Time */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    px: 3,
                    pt: '60px',
                    pb: 2,
                    bgcolor: 'transparent',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontSize: '15px',
                      fontWeight: 400,
                      letterSpacing: '-0.2px',
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.9)' 
                        : 'rgba(255, 255, 255, 0.95)',
                      mb: 0.5,
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </Typography>
                  <Typography 
                    variant="h3"
                    sx={{ 
                      fontSize: '82px',
                      fontWeight: 200,
                      lineHeight: 1,
                      letterSpacing: '-4px',
                      color: theme.palette.mode === 'dark' 
                        ? '#fff' 
                        : '#fff',
                      textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    9:41
                  </Typography>
                </Box>

                {/* iOS Status Bar (Right Side) */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 2,
                  }}
                >
                  {/* Signal bars */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.25, mr: 0.5 }}>
                    <Box sx={{ width: 3, height: 4, bgcolor: '#fff', borderRadius: '1px 1px 0 0', opacity: 0.9 }} />
                    <Box sx={{ width: 3, height: 6, bgcolor: '#fff', borderRadius: '1px 1px 0 0', opacity: 0.9 }} />
                    <Box sx={{ width: 3, height: 8, bgcolor: '#fff', borderRadius: '1px 1px 0 0', opacity: 0.9 }} />
                    <Box sx={{ width: 3, height: 10, bgcolor: '#fff', borderRadius: '1px 1px 0 0', opacity: 0.9 }} />
                  </Box>
                  {/* WiFi icon */}
                  <Box sx={{ 
                    width: 15, 
                    height: 11, 
                    position: 'relative',
                    opacity: 0.9,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: 0,
                      height: 0,
                      borderLeft: '3px solid transparent',
                      borderRight: '3px solid transparent',
                      borderBottom: '4px solid #fff',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderBottom: '5px solid #fff',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      opacity: 0.5,
                    }
                  }} />
                  {/* Battery */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5, opacity: 0.9 }}>
                    <Box sx={{ 
                      width: 24, 
                      height: 11, 
                      border: '1px solid #fff',
                      borderRadius: '2px',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: -3,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 1.5,
                        height: 6,
                        bgcolor: '#fff',
                        borderRadius: '0 1px 1px 0',
                      }
                    }}>
                      <Box sx={{ 
                        width: '75%', 
                        height: '100%', 
                        bgcolor: '#fff',
                        borderRadius: '1px',
                      }} />
                    </Box>
                  </Box>
                </Box>

                {/* iOS Lock Screen Content Area */}
                <Box
                  sx={{
                    flex: 1,
                    px: 3,
                    pt: 1,
                    pb: 8,
                    height: 'calc(100% - 200px)',
                    overflow: 'auto',
                    position: 'relative',
                    zIndex: 1,
                    '&::-webkit-scrollbar': {
                      width: 0,
                      display: 'none',
                    },
                  }}
                >
                  {/* iOS Lock Screen Notification Preview */}
                  {(notification.title || notification.message) ? (
                    <Box
                      sx={{
                        mt: 3,
                        borderRadius: '16px',
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.15)' 
                          : 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                        border: theme.palette.mode === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.2)'
                          : '1px solid rgba(255, 255, 255, 0.8)',
                        overflow: 'hidden',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                          : '0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          {/* App Icon with Badge */}
                          <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '11px',
                                bgcolor: theme.palette.mode === 'dark' 
                                  ? 'rgba(255, 255, 255, 0.2)' 
                                  : 'rgba(255, 255, 255, 0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                border: theme.palette.mode === 'dark'
                                  ? '1px solid rgba(255, 255, 255, 0.1)'
                                  : '1px solid rgba(0, 0, 0, 0.05)',
                              }}
                            >
                              {!logoError ? (
                                <Box
                                  component="img"
                                  src="/images/logo.png"
                                  alt="Utilita"
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                  onError={() => setLogoError(true)}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: getTypeColor(notification.type),
                                    color: 'white',
                                  }}
                                >
                                  {getTypeIcon(notification.type)}
                                </Box>
                              )}
                            </Box>
                            {/* Notification Badge */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -2,
                                right: -2,
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: '#34C759',
                                border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.7)'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'white',
                                }}
                              />
                            </Box>
                          </Box>
                          
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* App Name and Time */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.9)' 
                                    : 'rgba(0, 0, 0, 0.9)',
                                  letterSpacing: '-0.2px',
                                }}
                              >
                                Utilita
                                {notification.title && (
                                  <Typography 
                                    component="span" 
                                    sx={{ 
                                      fontSize: '13px',
                                      fontWeight: 400,
                                      color: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.7)' 
                                        : 'rgba(0, 0, 0, 0.6)',
                                      ml: 0.5,
                                    }}
                                  >
                                    • {notification.title}
                                  </Typography>
                                )}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.7)' 
                                    : 'rgba(0, 0, 0, 0.6)',
                                  letterSpacing: '-0.1px',
                                }}
                              >
                                now
                              </Typography>
                            </Box>
                            
                            {/* Notification Message */}
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '15px',
                                lineHeight: 1.4,
                                color: theme.palette.mode === 'dark' 
                                  ? 'rgba(255, 255, 255, 0.95)' 
                                  : 'rgba(0, 0, 0, 0.9)',
                                wordBreak: 'break-word',
                                letterSpacing: '-0.2px',
                              }}
                            >
                              {notification.message || notification.title || 'Notification message will appear here...'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 2,
                        p: 4,
                        textAlign: 'center',
                        borderRadius: '14px',
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)',
                        border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    >
                      <NotificationIcon
                        sx={{
                          fontSize: 56,
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.3)' 
                            : 'rgba(0, 0, 0, 0.3)',
                          mb: 1.5,
                        }}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.5)' 
                            : 'rgba(0, 0, 0, 0.5)',
                          fontSize: '14px',
                        }}
                      >
                        Start typing to see notification preview
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* iOS Lock Screen Bottom Controls */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 4,
                    zIndex: 2,
                  }}
                >
                  {/* Flashlight Button */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.15)'
                        : 'rgba(255, 255, 255, 0.25)',
                      backdropFilter: 'blur(20px)',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.2)'
                        : '1px solid rgba(255, 255, 255, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        border: '2px solid #fff',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#fff',
                        }
                      }}
                    />
                  </Box>
                  
                  {/* Camera Button */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.15)'
                        : 'rgba(255, 255, 255, 0.25)',
                      backdropFilter: 'blur(20px)',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.2)'
                        : '1px solid rgba(255, 255, 255, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: '2px solid #fff',
                        borderRadius: '4px',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          border: '2px solid #fff',
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* iOS Home Indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 134,
                    height: 5,
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    borderRadius: '3px',
                    zIndex: 1,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PushNotifications; 